/**
 * 작성자 : 김광민
 * 날짜 : 2023.11.29
 * 경로 : 센서 관리 - 다중 유해가스 측정기
 */
import { RefObject, useEffect, useState, useMemo } from 'react';
import { toast } from 'react-toastify';
import { PulseLoader } from 'react-spinners';
import { useTranslation } from 'react-i18next';
import { useRecoilValue } from 'recoil';
import { useLocation, useNavigate, useOutletContext } from 'react-router-dom';
import styled from 'styled-components';

import { IUser, pageInfoState, userState } from '../../atoms';
import { BtnGhost, BtnGray } from '../../components/Button';
import { LIST_COUNT, O2_RANGE, SENSOR_TIME } from '../../_constants';
import { SearchOptions } from '../../assets/styles/SearchOptions';
import Tab from '../../components/Tab';
import { IComCdList, ISensorRange, ITabObject } from 'customTypes';
import { setComCdListState } from '../../utils/setComCdListState';
import { todayYYYYMMDD } from '../../utils/formatDate';
import Portal from '../../components/Portal';
import RangePicker, { onChangeRangeInput } from '../../components/RangePicker';
import dayjs from 'dayjs';
import LineChartModal from '../../components/Modal/lineChartModal';
import useOnKeydownF9 from '../../utils/useOnKeydownF9';
import { useSetAuth } from '../../utils/useSetAuth';
import { logPost } from '../../services/log';
import SelectBox from '../../components/SelectBox';
import illustrator from '../../assets/images/illustration/31.svg';
import nodataIllustrator from '../../assets/images/illustration/304.svg';
import i18n from '../../translation/i18n';
import { LoadingModalBackground } from '../../assets/styles/Modal';
import BackButton from '../../components/BackButton';
import useSetListCount from '../../utils/useSetListCount';
import TuiGridTable from '../../components/TuiGridTable';
import { useQuery } from '@tanstack/react-query';
import { apiGet } from '../../services/_common';
import ShortcutButton from '../../components/button/ShortcutButton';
import IssueGuide from '../../components/IssueGuide';
import { ContentsContainerRoot } from '../../assets/styles/ContentsContainerRoot';
import SearchFilterModal from '../../components/Modal/SearchFilterModal';
import { showExcelButton } from '../../utils/deviceAuth';
import { convertCO2PpmToPercent, convertCO2PpmToMgPerCubicMeter } from '../../utils/convertCO2Unit';

const Root = styled(ContentsContainerRoot)`
  --good: var(--primary);
  --caution: var(--filled-green);
  --warning: var(--filled-amber);
  --danger: var(--filled-red);

  width: 100%;

  &.headerLeft {
    tbody {
      tr {
        th:last-child {
          text-align: left !important;
        }
      }
    }
  }
  &.hideRoot {
    visibility: hidden;
    position: absolute;
  }
  .tui-container {
    flex-grow: 1;
    overflow: hidden;
    margin-left: 0.5rem;
  }
  .loader {
    display: flex;
    width: 100%;
    height: 100%;
  }
  .emptyData {
    flex-grow: 1;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    user-select: none;
    padding-bottom: 4rem;
    gap: 1rem;
    img {
      width: 14rem;
      /* user-drag: none; */
      -webkit-user-drag: none;
    }
    span {
      color: var(--filled-violet);
    }
  }
  .inputFormsWrapper {
    background-color: var(--board);
    border-radius: 0.5rem;
    width: 100%;
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
  }
  .searchContent {
    display: flex;
    justify-content: space-between;
    .buttonsWrapper {
      display: flex;
      margin-bottom: 0rem;
    }
  }

  .end {
    justify-content: end;
    > button {
      margin-left: 1rem;
    }
  }

  .thead > .tr {
    > div {
      display: flex;
      align-items: center;
      justify-content: center;
    }

    .spaceBetween {
      justify-content: space-evenly;
    }
  }

  .table {
    height: 64rem;
    padding: 0;

    > .tbody > .tr {
      padding: 0.4rem 1rem;
    }
  }

  .tab {
    padding: 0 1rem;
    cursor: pointer;
    height: 3rem;
  }

  .activeTab {
    font-weight: bold;
    border-bottom: 2px solid rgb(63, 66, 84);
  }

  .marginBottomNone {
    margin-bottom: 0;
  }

  .tui-grid-filter-dropdown select {
    box-sizing: border-box;
  }

  @media screen and (max-width: 767px) {
    .searchContent {
      flex-direction: column;
      > div {
        width: 100% !important;
      }
    }
  }
  .range-wrapper {
    width: 420px;

    .range-top {
      display: flex;
      align-items: center;
      justify-content: center;
      margin-left: -8px;

      .range-status {
        display: flex;
        align-items: center;
        font-size: 0.9rem;
        width: 72px;
        margin-right: 12px;

        &::before {
          content: '●';
          font-size: 1.1rem;
          margin-right: 4px;
        }

        &:nth-child(1)::before {
          color: var(--good);
        }
        &:nth-child(2)::before {
          color: var(--caution);
        }
        &:nth-child(3)::before {
          color: var(--warning);
        }
        &:nth-child(4) {
          margin-right: 0;

          &::before {
            color: var(--danger);
          }
        }
      }
    }
    .range-bottom {
      display: flex;
      justify-content: space-between;
      align-items: center;
      width: 100%;

      .range-left {
        display: flex;
        font-size: 14px;
        color: var(--text-tertiary);
      }

      .range-center {
        display: flex;
        align-items: center;

        .range-guage-item {
          width: 72px;
          font-size: 0.925rem;
          margin-right: 12px;

          hr {
            border: none;
            height: 1px;
          }

          .guage-item-top {
            display: flex;
            justify-content: space-between;
          }
          .guage-item-bottom {
            display: flex;
            justify-content: space-between;
          }
          &:nth-child(1) {
            color: var(--good);
            hr {
              background: var(--good);
            }
          }

          &:nth-child(2) {
            color: var(--caution);
            hr {
              background: var(--caution);
            }
          }

          &:nth-child(3) {
            color: var(--warning);
            hr {
              background: var(--warning);
            }
          }

          &:nth-child(4) {
            color: var(--danger);
            margin-right: 0;
            hr {
              background: var(--danger);
            }
          }
        }
      }

      .range-right {
        font-size: 14px;
        color: var(--text-tertiary);
      }
    }
  }
  .value {
    display: flex;
    justify-content: center;
    align-items: center;
    gap: 0.25rem;
    &::before {
      content: '●';
      font-size: 1.5rem;
      padding-top: 2px;
    }
    &.level {
      .value-title {
        color: var(--text-tertiary);
      }
      &::before {
        color: transparent;
      }
    }
    &.level0 {
      .value-title {
        color: var(--primary);
      }
      &::before {
        color: var(--primary);
      }
    }
    &.level1 {
      .value-title {
        color: var(--filled-green);
      }
      &::before {
        color: var(--filled-green);
      }
    }
    &.level2 {
      .value-title {
        color: var(--filled-amber);
      }
      &::before {
        color: var(--filled-amber);
      }
    }
    &.level3 {
      .value-title {
        color: var(--filled-red);
      }
      &::before {
        color: var(--filled-red);
      }
    }
  }

  .secondSearchOption .guide.levels {
    flex-shrink: 0;
    display: flex;
    gap: 1rem;
    font-size: 0.875rem;
    margin: 0 0.5rem;
    @media (min-width: 768px) {
      margin-right: 1rem;
    }
    > div.value {
      > div {
        > span {
          line-height: 1rem;
          &:nth-child(2) {
            color: var(--text-tertiary);
          }
        }
      }
    }
  }

  .mgas-responsive {
    flex-wrap: nowrap;
    @media (min-width: 1025px) {
      flex-wrap: wrap;
      overflow: visible !important;
    }
  }
`;

const Ssensor7 = () => {
  const location = useLocation();
  const tabInfo = useRecoilValue(pageInfoState);
  const toDay = todayYYYYMMDD();
  const userInfo = useRecoilValue<IUser>(userState);
  const { hCd, sCd } = userInfo;
  const size = useOutletContext<any>();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const INIT_F_NAME = { type: 'fName', fName: 'A', cdName: t('전체') };
  const { auth } = useSetAuth(); // 사용자 권한값 훅
  const { isF9Pressed, setIsF9Pressed } = useOnKeydownF9(); // f9키 프레스 훅
  const { listCount, setListCount, patchUserMenuAPI } = useSetListCount(); // 검색줄수설정 훅
  const { userMenuList } = userInfo;
  const { defaultMrCd } = useRecoilValue(pageInfoState);
  const [rowState, setRowState] = useState<any>({});
  const [viewRowState, setViewRowState] = useState<boolean>(false);
  const [exportTableState, setExportTableState] = useState<any[]>([]);
  const [mainColumns, setMainColumns] = useState<any[]>([]);
  const [subColumns, setSubColumns] = useState<any[]>([]);
  const [exportColumns, setExportColumns] = useState<any[]>([]);
  const [currentTabMrCd, setCurrentTabMrCd] = useState(defaultMrCd);
  const [visibleRangePicker, setVisibleRangePicker] = useState(false);
  const [listCountComCdList, setListCountComCdList] = useState<IComCdList[]>([]); // 검색줄수 공통코드
  const [fNameList, setFnameList] = useState<any[]>([]);
  const [fName, setFName] = useState(INIT_F_NAME);
  const [excelBtn, setExcelBtn] = useState(false);
  const [excelBtn2, setExcelBtn2] = useState(false);
  const [mainChartDataReverse, setMainChartDataReverse] = useState<any>([]);
  const [subChartDataReverse, setSubChartDataReverse] = useState<any>([]);
  const [openModal, setOpenModal] = useState<any>({ status: false, type: '' });
  const [newTabList, setNewTabList] = useState<ITabObject[]>([]);
  const [mGasInfo, setMGasInfo] = useState({ type: '1', name: t('산소'), tableKey: { avg: 'avgO2', max: 'maxO2', min: 'minO2', value: 'O2', valueType: 'VOL' } });
  const [searchOption, setSearchOption] = useState({ start: toDay, end: toDay });
  const [sensorTime, setSensorTime] = useState<any>({ type: 'time', time: '1', cdName: t('시간') });
  const [rangeState, setRangeState] = useState([{ startDate: new Date(), endDate: new Date(), key: 'selection' }]);
  const showExcelBtn = showExcelButton(auth.excelAuth);

  // CO2 단위(unit)값에 따라 헤더 표기(valueType) 변환 (CO2에만 적용)
  const getCO2ValueType = (unit?: string) => {
    switch (unit) {
      case '%':
        return '%';
      case 'mg/m³':
        return 'mg/m³';
      default:
        return 'PPM';
    }
  };

  // CO2 ppm 값을 unit에 따라 변환하는 함수
  const convertCO2Value = (ppmValue: number | string | undefined, tabMrCd?: string): number => {
    if (ppmValue === undefined || ppmValue === null || ppmValue === '') {
      return 0;
    }
    const numValue = typeof ppmValue === 'string' ? Number(ppmValue) : ppmValue;
    if (isNaN(numValue)) {
      return 0;
    }

    // 현재 탭 정보 확인
    const currentTabInfo = tabMrCd ? getMgasInfo(tabMrCd) : mGasInfo;

    // CO2 탭이 아니면 그대로 반환
    if (currentTabInfo.tableKey.value !== 'CO2') {
      return numValue;
    }

    // allSensorRanges에서 현재 탭의 range 정보 가져오기
    let currentRange = null;
    if (tabMrCd && allSensorRanges) {
      // tabMrCd가 전달되면 allSensorRanges에서 직접 가져오기
      const currentTabInfoForRange = getMgasInfo(tabMrCd);
      currentRange = allSensorRanges[currentTabInfoForRange.tableKey.value];
    } else {
      // tabMrCd가 없으면 현재 sensorRange 사용
      currentRange = sensorRange;
    }

    if (!currentRange?.unit) {
      return numValue;
    }

    const { unit } = currentRange;
    switch (unit) {
      case '%':
        return convertCO2PpmToPercent(numValue);
      case 'mg/m³':
      case 'mg':
        return convertCO2PpmToMgPerCubicMeter(numValue);
      case 'ppm':
      default:
        return numValue;
    }
  };

  // 현재 탭에 type과 name, table에 사용하는 key값 구하는 함수
  const getMgasInfo = (mrCd: string) => {
    switch (mrCd) {
      case '001':
        return { type: '1', name: t('산소'), tableKey: { avg: 'avgO2', max: 'maxO2', min: 'minO2', value: 'O2', valueType: '% vol' }, status: '' };
      case '002':
        return { type: '2', name: t('이산화질소'), tableKey: { avg: 'avgNO2', max: 'maxNO2', min: 'minNO2', value: 'NO2', valueType: 'PPM' }, status: '' };
      case '003':
        return { type: '3', name: t('일산화탄소'), tableKey: { avg: 'avgCO', max: 'maxCO', min: 'minCO', value: 'CO', valueType: 'PPM' }, status: '' };
      case '004':
        return { type: '4', name: t('이산화탄소'), tableKey: { avg: 'avgCO2', max: 'maxCO2', min: 'minCO2', value: 'CO2', valueType: 'PPM' }, status: '' };
      case '005':
        return { type: '5', name: t('황화수소'), tableKey: { avg: 'avgH2S', max: 'maxH2S', min: 'minH2S', value: 'H2S', valueType: 'PPM' }, status: '' };
      case '006':
        return { type: '6', name: t('메탄'), tableKey: { avg: 'avgCH4', max: 'maxCH4', min: 'minCH4', value: 'CH4', valueType: '% LEL' }, status: '' };
      default:
        return { type: '1', name: t('산소'), tableKey: { avg: 'avgO2', max: 'maxO2', min: 'minO2', value: 'O2', valueType: '% vol' }, status: '' };
    }
  };

  // 모든 센서의 range 정보를 가져오는 쿼리
  const {
    data: allSensorRanges,
    isLoading: isLoadingAllSensorRanges,
    isFetching: isFetchingAllSensorRanges,
    isError: isErrorAllSensorRanges,
  } = useQuery([`allSensorRangesGet`, hCd, sCd], () => fetchAllSensorRanges(), { enabled: !!hCd && !!sCd, cacheTime: 0 });

  // 현재 탭의 센서 range 정보를 allSensorRanges에서 추출
  const sensorRange = useMemo(() => {
    if (!allSensorRanges || !mGasInfo.tableKey.value) return null;
    const range = allSensorRanges[mGasInfo.tableKey.value];
    if (!range) return null;
    return {
      level1: range.level1,
      level2: range.level2,
      level3: range.level3,
      srCd: mGasInfo.tableKey.value,
      unit: range.unit,
    };
  }, [allSensorRanges, mGasInfo.tableKey.value]);

  const fetchAllSensorRanges = async () => {
    try {
      const res = await apiGet({ path: '/sensor/range', req: { hCd, sCd } });
      const rangeMap: { [key: string]: { level1: number; level2: number; level3: number; unit?: string } } = {};
      res.data.data.rangeList.forEach((list: ISensorRange) => {
        rangeMap[list.srCd] = {
          level1: list.level1 ? Number(list.level1) : 0,
          level2: list.level2 ? Number(list.level2) : 0,
          level3: list.level3 ? Number(list.level3) : 0,
          unit: list.unit,
        };
      });
      return rangeMap;
    } catch (error) {
      console.error(error);
      throw new Error('error');
    }
  };

  const mainDataSet = [
    {
      label: t('평균'),
      data: `${getMgasInfo(currentTabMrCd).tableKey.avg}`,
      borderColor: 'rgba(0, 221, 255, 0.5)',
      backgroundColor: 'rgba(0, 221, 255, 0.5)',
    },
    {
      label: t('최소'),
      data: `${getMgasInfo(currentTabMrCd).tableKey.min}`,
      borderColor: 'rgba(0, 119, 255, 0.5)',
      backgroundColor: 'rgba(0, 119, 255, 0.5)',
    },
    {
      label: t('최대'),
      data: `${getMgasInfo(currentTabMrCd).tableKey.max}`,
      borderColor: 'rgba(0, 0, 255, 0.5)',
      backgroundColor: 'rgba(0, 0, 255, 0.5)',
    },
  ];

  const subDataSet = [
    {
      label: t(mGasInfo.name),
      data: `${mGasInfo.tableKey.value}`,
      borderColor: 'rgba(0, 221, 255, 0.5)',
      backgroundColor: 'rgba(0, 221, 255, 0.5)',
    },
  ];

  useEffect(() => {
    const arr: any[] = [];
    userMenuList.map((v: any) => {
      const result = tabInfo?.tabList.find((el: ITabObject) => el.mtCd === v.mtCd && el.mdCd === v.mdCd && el.mrCd === v.mrCd);
      if (result) {
        arr.push(result);
        setNewTabList(arr);
      }
    });
  }, [tabInfo.tabList]);

  useEffect(() => {
    if (!rangeState[0].startDate) {
      return;
    }
    setSearchOption((prev: any) => ({ ...prev, start: dayjs(rangeState[0].startDate).format('YYYY-MM-DD') }));
  }, [rangeState[0].startDate]);

  useEffect(() => {
    if (!rangeState[0].endDate) {
      return;
    }
    setSearchOption((prev: any) => ({ ...prev, end: dayjs(rangeState[0].endDate).format('YYYY-MM-DD') }));
  }, [rangeState[0].endDate]);

  const tuiFormatter = (value: any) => {
    const localeStringValue = Number(value.value)?.toLocaleString();
    return localeStringValue;
  };

  useEffect(() => {
    const headerValueType = mGasInfo.tableKey.value === 'CO2' ? getCO2ValueType(sensorRange?.unit) : mGasInfo.tableKey.valueType;

    const defaultExportColumns = [
      {
        header: t('장비명'),
        name: 'dvName',
        align: 'left',
        sortable: true,
        width: 120,
        renderer: { classNames: ['text_secondary'] },
      },
      {
        header: t('측정일자/시간'),
        name: 'checkTime',
        align: 'center',
        sortable: true,
        minWidth: 140,
        renderer: { classNames: ['text_secondary'] },
      },
      {
        header: `${headerValueType}`,
        name: `${mGasInfo.tableKey.value}`,
        align: 'center',
        sortable: true,
        filter: 'number',
        minWidth: 120,
      },
    ];
    setMainColumns([
      {
        header: t('장비명'),
        name: 'dvName',
        align: 'left',
        sortable: true,
        filter: 'select',
        width: 120,
        renderer: { classNames: ['text_secondary'] },
      },
      {
        header: t('측정일자'),
        name: 'checkTime',
        align: 'center',
        sortable: true,
        filter: 'select',
        minWidth: 120,
        renderer: { classNames: ['text_secondary'] },
      },
      {
        header: t('평균'),
        name: `${mGasInfo.tableKey.avg}`,
        align: 'center',
        sortable: true,
        filter: 'number',
        minWidth: 120,
      },
      {
        header: t('최소'),
        name: `${mGasInfo.tableKey.min}`,
        align: 'center',
        sortable: true,
        filter: 'number',
        minWidth: 120,
      },
      {
        header: t('최대'),
        name: `${mGasInfo.tableKey.max}`,
        align: 'center',
        sortable: true,
        filter: 'number',
        minWidth: 120,
      },
    ]);
    setSubColumns([
      {
        header: t('장비명'),
        name: 'dvName',
        align: 'left',
        sortable: true,
        width: 120,
        renderer: { classNames: ['text_secondary'] },
      },
      {
        header: t('측정일자/시간'),
        name: 'checkTime',
        align: 'center',
        sortable: true,
        minWidth: 140,
        renderer: { classNames: ['text_secondary'] },
      },
      {
        header: `${headerValueType}`,
        name: `${mGasInfo.tableKey.value}_color`,
        align: 'center',
        sortable: false,
        width: 40,
      },
      {
        header: ` `,
        name: `${mGasInfo.tableKey.value}`,
        align: 'left',
        sortable: true,
        filter: 'number',
        width: 120,
        formatter: (value: string) => tuiFormatter(value),
      },
    ]);

    // subtable에서 보기 설정에 따라 엑셀 다운로드시 상태 값 넣어줄지 뺄지 정하는 로직
    if (sensorTime.time !== '1') {
      setExportColumns([
        ...defaultExportColumns,
        {
          header: t('상태'),
          name: `${mGasInfo.tableKey.value}_status`,
          align: 'center',
          sortable: true,
          filter: 'number',
          minWidth: 120,
        },
      ]);
    } else {
      setExportColumns([...defaultExportColumns]);
    }
  }, [i18n.language, mGasInfo.type, mGasInfo.tableKey.value, sensorRange?.unit, sensorTime.time]);

  const {
    data: mainSensor,
    isLoading: isLoadingMainSensor,
    isFetching: isFetchingMainSensor,
    isError: isErrorMainSensor,
    refetch: refetchMainSensor,
  } = useQuery([`mainSensorGet_${currentTabMrCd}`, currentTabMrCd], () => fetchMainSensorData(), {
    enabled: !!hCd && !!sCd,
    cacheTime: 0,
  });

  const fetchMainSensorData = async () => {
    try {
      const { start, end } = searchOption;
      const req = {
        hCd,
        sCd,
        dvNo: fName.fName === 'A' ? '' : fName.fName,
        date1: start.replaceAll('-', ''),
        date2: end.replaceAll('-', ''),
        type: getMgasInfo(currentTabMrCd).type,
      };
      const res = await apiGet({ path: '/sensor/mgas', req });
      const dvList = toDvList(mutateData(res.data.data, currentTabMrCd));
      const newData = dvList.map((v: any) => {
        return mutateData(res.data.data, currentTabMrCd).filter((v2: any) => v2.dvNo === v.dvNo);
      });
      // 차트: 오름차순 (오래된 일자 먼저)
      setMainChartDataReverse(newData.map((v3: any) => v3.reverse()));

      logPost({
        hCd,
        sCd,
        userId: userInfo.userId,
        menu: `${mGasInfo.name} 측정기`,
        action: '조회',
        etc: ``,
      });

      return mutateData(res.data.data, currentTabMrCd);
    } catch (error) {
      console.error(error);
      throw new Error('error');
    }
  };

  const {
    data: subSensor,
    isLoading: isLoadingSubSensor,
    isFetching: isFetchingSubSensor,
    isError: isErrorSubSensor,
    refetch: refetchSubSensor,
  } = useQuery([`subSensorGet_${sensorTime.time}_${rowState.rowKey}`, sensorTime.time, rowState.rowKey], () => fetchSubSensorData(), {
    enabled: !!hCd && !!sCd,
  });

  const fetchSubSensorData = async () => {
    if (rowState.rowKey === undefined) return [];
    try {
      const req = {
        hCd,
        sCd,
        dvNo: rowState.dvNo,
        date: rowState.checkTime?.replaceAll('-', ''),
        time: sensorTime.time,
        type: mGasInfo.type,
      };
      const res = await apiGet({ path: '/sensor/mgasDay', req });

      if (res.status === 200) {
        if (sensorTime.time === '1') {
          // 테이블/엑셀: 내림차순 (최신일자 먼저)
          setExportTableState(
            mutateSubData(res.data.data, currentTabMrCd).map((v: any) => {
              return {
                ...v,
                checkTime: `${v.checkTime}${t('시')}`,
              };
            })
          );
          // 차트: 오름차순 (오래된 일자 먼저)
          const chartData = mutateSubData(res.data.data, currentTabMrCd).map((el: any) => ({ ...el, checkTime: `${el.checkTime}${t('시')}` }));
          setSubChartDataReverse([chartData.reverse()]);
          // 테이블 표시: 내림차순 (최신일자 먼저)
          const result = mutateSubData(res.data.data, currentTabMrCd).map((v: any) => {
            return {
              ...v,
              checkTime: `${v.checkTime}${t('시')}`,
              O2: `${v.O2}`,
              NO2: `${v.NO2}`,
              CO: `${v.CO}`,
              CO2: `${v.CO2}`,
              H2S: `${v.H2S}`,
              CH4: `${v.CH4}`,
            };
          });
          return result;
        }
        // 테이블/엑셀: 내림차순 (최신일자 먼저)
        setExportTableState(
          res.data.data.map((v: any) => {
            const levelO2 = calculateLevel('O2', v.O2, currentTabMrCd);
            const levelNO2 = calculateLevel('NO2', v.NO2, currentTabMrCd);
            const levelCO = calculateLevel('CO', v.CO, currentTabMrCd);
            const levelCO2 = calculateLevel('CO2', v.CO2, currentTabMrCd);
            const levelH2S = calculateLevel('H2S', v.H2S, currentTabMrCd);
            const levelCH4 = calculateLevel('CH4', v.CH4, currentTabMrCd);
            return {
              ...v,
              O2: v.O2,
              NO2: v.NO2,
              CO: v.CO,
              CO2: convertCO2Value(v.CO2, currentTabMrCd),
              H2S: v.H2S,
              CH4: v.CH4,
              O2_status: `${getStatus(levelO2)}`,
              NO2_status: `${getStatus(levelNO2)}`,
              CO_status: `${getStatus(levelCO)}`,
              CO2_status: `${getStatus(levelCO2)}`,
              H2S_status: `${getStatus(levelH2S)}`,
              CH4_status: `${getStatus(levelCH4)}`,
            };
          })
        );
        // 차트: 오름차순 (오래된 일자 먼저) - reverse() 추가
        const chartData = res.data.data.map((v: any) => ({
          ...v,
          O2: v.O2,
          NO2: v.NO2,
          CO: v.CO,
          CO2: convertCO2Value(v.CO2, currentTabMrCd),
          H2S: v.H2S,
          CH4: v.CH4,
        }));
        setSubChartDataReverse([chartData.reverse()]);

        // 테이블 표시: 내림차순 (최신일자 먼저) - reverse() 제거
        const result = res.data.data.map((v: any) => {
          const levelO2 = calculateLevel('O2', v.O2, currentTabMrCd);
          const levelNO2 = calculateLevel('NO2', v.NO2, currentTabMrCd);
          const levelCO = calculateLevel('CO', v.CO, currentTabMrCd);
          const levelCO2 = calculateLevel('CO2', v.CO2, currentTabMrCd);
          const levelH2S = calculateLevel('H2S', v.H2S, currentTabMrCd);
          const levelCH4 = calculateLevel('CH4', v.CH4, currentTabMrCd);
          return {
            ...v,
            O2_color: `<span class='value ${getColor(levelO2)}' />`,
            NO2_color: `<span class='value ${getColor(levelNO2)}' />`,
            CO_color: `<span class='value ${getColor(levelCO)}' />`,
            CO2_color: `<span class='value ${getColor(levelCO2)}' />`,
            H2S_color: `<span class='value ${getColor(levelH2S)}' />`,
            CH4_color: `<span class='value ${getColor(levelCH4)}' />`,
            O2: v.O2,
            NO2: v.NO2,
            CO: v.CO,
            CO2: convertCO2Value(v.CO2, currentTabMrCd),
            H2S: v.H2S,
            CH4: v.CH4,
          };
        });
        return result;
      }
      return [];
    } catch (error) {
      console.error(error);
      throw new Error('error');
    }
  };

  useEffect(() => {
    setMGasInfo(getMgasInfo(currentTabMrCd));
  }, [currentTabMrCd]);

  useEffect(() => {
    getNormalCdAPI();
    setComCdListState(LIST_COUNT, setListCountComCdList, false);
  }, []);

  useEffect(() => {
    if (isF9Pressed) {
      onClickSearch();
      setIsF9Pressed(false);
    }
  }, [isF9Pressed]);

  // useEffect(() => {
  //   if (rowState) refetchSubSensor();
  // }, [rowState.rowKey, sensorTime.time]);

  const mutateData = (data: any, tabMrCd?: string) => {
    const result = data.map((element: any) => ({
      ...element,
      avgCH4: element.avgCH4,
      avgCO: element.avgCO,
      avgCO2: convertCO2Value(element.avgCO2, tabMrCd),
      avgData7: element.avgData7,
      avgData8: element.avgData8,
      avgData9: element.avgData9,
      avgData10: element.avgData10,
      avgH2S: element.avgH2S,
      avgNO2: element.avgNO2,
      avgO2: element.avgO2,
      maxCH4: element.maxCH4,
      maxCO: element.maxCO,
      maxCO2: convertCO2Value(element.maxCO2, tabMrCd),
      maxData7: element.maxData7,
      maxData8: element.maxData8,
      maxData9: element.maxData9,
      maxData10: element.maxData10,
      maxH2S: element.maxH2S,
      maxNO2: element.maxNO2,
      maxO2: element.maxO2,
      minCH4: element.minCH4,
      minCO: element.minCO,
      minCO2: convertCO2Value(element.minCO2, tabMrCd),
      minData7: element.minData7,
      minData8: element.minData8,
      minData9: element.minData9,
      minData10: element.minData10,
      minH2S: element.minH2S,
      minNO2: element.minNO2,
      minO2: element.minO2,
    }));
    return result;
  };

  // 장비 리스트 조회 API
  const getNormalCdAPI = async () => {
    const req = { hCd, sCd, grCd: '08' };
    const res = await apiGet({ path: '/sensor/detail', req });
    const { data, statusCode } = res.data !== undefined && res.data;
    if (statusCode === 200) {
      const newList = [];
      newList.push(INIT_F_NAME);
      data.sensorList.map((v: any) => {
        newList.push({ type: 'fName', fName: v.subCd, cdName: v.sName });
      });
      setFnameList(newList);
    } else {
      // toast.error(t(ERROR));
    }
  };

  // 센서 값과 range를 비교하여 level을 계산하는 함수
  const calculateLevel = (sensorCode: string, sensorValue: number | string | undefined, tabMrCd?: string): string => {
    if (!allSensorRanges || sensorValue === undefined || sensorValue === null || sensorValue === '') {
      return '';
    }

    const numValue = typeof sensorValue === 'string' ? Number(sensorValue) : sensorValue;
    if (isNaN(numValue)) {
      return '';
    }

    const range = allSensorRanges[sensorCode];
    if (!range || !range.level1 || !range.level2 || !range.level3) {
      return '';
    }

    const { level1, level2, level3 } = range;
    const valueToCompare = numValue;

    // 산소 경우 범위 계산
    if (sensorCode === 'O2') {
      const matchedRange = [...O2_RANGE].reverse().find((o2Range) => {
        const [lowerMin, lowerMax] = o2Range.lower;
        const [upperMin, upperMax] = o2Range.upper;

        // 하한선(lower) 체크: -1은 무시, 값이 범위 내에 있는지 확인
        const isLowerBreached = (lowerMin !== -1 && valueToCompare < lowerMin) || (lowerMax !== -1 && valueToCompare <= lowerMax);

        // 상한선(upper) 체크: -1은 무시
        const isUpperBreached = (upperMin !== -1 && valueToCompare > upperMin) || (upperMax !== -1 && valueToCompare >= upperMax);

        return isLowerBreached || isUpperBreached;
      });

      return matchedRange ? String(matchedRange.level) : '0';
    }

    // CO2의 경우 변환된 값으로 비교
    if (sensorCode === 'CO2' && range.unit) {
      const co2Value = convertCO2Value(numValue);
      if (co2Value < level1) return '0';
      if (co2Value >= level1 && co2Value < level2) return '1';
      if (co2Value >= level2 && co2Value < level3) return '2';
      return '3';
    }
    // if (sensorCode === 'CO2' && range.unit) {
    //   valueToCompare = convertCO2Value(numValue, tabMrCd);
    // }

    // level0 (좋음): 값 < level1
    // level1 (주의): level1 <= 값 < level2
    // level2 (경고): level2 <= 값 < level3
    // level3 (위험): level3 <= 값
    if (valueToCompare < level1) {
      return '0';
    }
    if (valueToCompare >= level1 && valueToCompare < level2) {
      return '1';
    }
    if (valueToCompare >= level2 && valueToCompare < level3) {
      return '2';
    }
    return '3';
  };

  const getColor = (value: string) => {
    if (value === '0') return 'level0';
    if (value === '1') return 'level1';
    if (value === '2') return 'level2';
    if (value === '3') return 'level3';
    return 'level';
  };

  const getStatus = (value: string) => {
    if (value === '0') return t('좋음');
    if (value === '1') return t('주의');
    if (value === '2') return t('경고');
    if (value === '3') return t('위험');
    return '';
  };

  const mutateSubData = (data: any, tabMrCd?: string) => {
    const result = data.map((element: any) => ({
      ...element,
      CH4: element.CH4,
      CO: element.CO,
      CO2: convertCO2Value(element.CO2, tabMrCd),
      H2S: element.H2S,
      NO2: element.NO2,
      O2: element.O2,
    }));
    return result;
  };

  const toDvList = (data: any[]) => {
    const map = new Map(data.map((m: any) => [m.dvNo, m])).entries();
    return Array.from(map).map((v: any) => {
      return { dvNo: v[1].dvNo, dvName: v[1].dvName };
    });
  };

  // 검색 버튼 클릭
  const onClickSearch = (filterRef?: RefObject<HTMLDivElement>) => {
    if (!openModal.status || (openModal.status && openModal.type === 'filter')) {
      if (searchOption.end && searchOption.start > searchOption.end) {
        toast.warning(t('날짜를 다시 입력하세요'));
        setSearchOption((prev: any) => ({ ...prev, end: '' }));
      } else {
        if (filterRef) {
          // 모바일일 경우 검색 필터 모달 창 닫기
          filterRef.current?.classList.remove('active');
          setOpenModal({ status: false, type: 'filter' });
        }
        refetchMainSensor();
        setViewRowState(false);
        setVisibleRangePicker(false);
        navigate({ pathname: location.pathname, search: '' });
      }
    }
  };

  // 초기화 버튼 클릭
  const onClickInitiateSearchOption = () => {
    Object.keys(searchOption).map((el: any) => {
      return setSearchOption((prev: any) => ({ ...prev, [el]: '', start: toDay, end: toDay }));
    });
    setFName(INIT_F_NAME);
    setRangeState([{ startDate: new Date(), endDate: new Date(), key: 'selection' }]);
    setViewRowState(false);
  };

  // 차트 버튼 클릭
  const onClickChart = ({ tableData, type }: { tableData: any[]; type: any }) => {
    if (tableData.length !== 0) setOpenModal({ status: true, type });
    else toast.warning(t('데이터가 없습니다'));
  };

  // 엑셀 저장 버튼 클릭
  const onClickExcelSave = () => {
    if (mainSensor.length <= 0) return;
    setExcelBtn(true);
    logPost({
      hCd,
      sCd,
      userId: userInfo.userId,
      menu: `${mGasInfo.name} 측정기`,
      action: '엑셀 저장',
      etc: `${searchOption.start}~${searchOption.end}`,
    });
  };

  // 엑셀 저장 버튼 클릭
  const onClickExcelSave2 = () => {
    if (subSensor?.length <= 0) return;
    setExcelBtn2(true);
    logPost({
      hCd,
      sCd,
      userId: userInfo.userId,
      menu: `${mGasInfo.name} 측정기`,
      action: '엑셀 저장',
      etc: `${rowState.checkTime}`,
    });
  };

  // 인쇄 버튼 클릭
  const onClickPrint = () => {
    //   const handlePrint = useReactToPrint({
    //     content: () => componentRef.current,
    //   });
    // handlePrint();
  };

  // 엑셀 버튼
  const renderExcelBtn = (onClickBtn: any) => {
    if (showExcelBtn) {
      return (
        <BtnGhost onClick={onClickBtn}>
          <span className='icon-ms-xlsx'>X</span> {t('엑셀')}
        </BtnGhost>
      );
    }
    return null;
  };

  // 프린트 버튼
  const renderPrintBtn = (clickPrint: any) => {
    if (auth.printAuth) {
      return <BtnGray onClick={clickPrint}>{t('인쇄')}</BtnGray>;
    }
    return null;
  };

  // 측정기 Row 클릭
  const onClickRow = async (rowData: any): Promise<void> => {
    if (rowData) {
      setRowState(rowData);
      setViewRowState(true);
    }
  };

  const onClickRangeInput = () => {
    if (!visibleRangePicker) setVisibleRangePicker(true);
  };

  const onClickTab = (tab: string) => {
    setSensorTime({
      type: 'time',
      time: '1',
      cdName: t('시간'),
    });
    setViewRowState(false);
    setRowState({});
    setCurrentTabMrCd(tab);
  };

  // 필터 버튼 클릭
  const onClickFilter = () => {
    setOpenModal({ status: true, type: 'filter' });
  };

  // subTable 상단 평균/최소/최대 값을 구하는 함수
  const getRowStateInfo = (key: string) => {
    const { value } = mGasInfo.tableKey;
    if (key === 'avg' && value === 'O2') return rowState.avgO2;
    if (key === 'min' && value === 'O2') return rowState.minO2;
    if (key === 'max' && value === 'O2') return rowState.maxO2;
    if (key === 'avg' && value === 'NO2') return rowState.avgNO2;
    if (key === 'min' && value === 'NO2') return rowState.minNO2;
    if (key === 'max' && value === 'NO2') return rowState.maxNO2;
    if (key === 'avg' && value === 'CO') return rowState.avgCO;
    if (key === 'min' && value === 'CO') return rowState.minCO;
    if (key === 'max' && value === 'CO') return rowState.maxCO;
    if (key === 'avg' && value === 'CO2') return rowState.avgCO2;
    if (key === 'min' && value === 'CO2') return rowState.minCO2;
    if (key === 'max' && value === 'CO2') return rowState.maxCO2;
    if (key === 'avg' && value === 'H2S') return rowState.avgH2S;
    if (key === 'min' && value === 'H2S') return rowState.minH2S;
    if (key === 'max' && value === 'H2S') return rowState.maxH2S;
    if (key === 'avg' && value === 'CH4') return rowState.avgCH4;
    if (key === 'min' && value === 'CH4') return rowState.minCH4;
    if (key === 'max' && value === 'CH4') return rowState.maxCH4;
    return null;
  };

  const getFileName = (tableType: 'main' | 'sub') => {
    const type = mGasInfo.name;
    const detail = sensorTime.time === '1' ? t('시간') : t('상세');
    const startDate = rangeState[0].startDate ? dayjs(rangeState[0].startDate).format('YYYY-MM-DD') : '';
    const endDate = dayjs(rangeState[0].endDate).format('YYYY-MM-DD');
    const period = startDate !== '' ? (startDate === endDate ? startDate : `${startDate}~${endDate}`) : t('전체기간');

    if (tableType === 'sub') return `${type}(${detail})_${rowState.checkTime}`;
    return `${type}(${t('종합')})_${period}`;
  };

  const renderSubTable = () => {
    if (isErrorAllSensorRanges || isErrorSubSensor) return <IssueGuide />;

    // loading 중일때
    if (isLoadingAllSensorRanges || isFetchingAllSensorRanges) {
      return (
        <Root loading={isLoadingAllSensorRanges || isFetchingAllSensorRanges}>
          <div className='loader'>
            <PulseLoader className='flex-center' color='rgb(0, 122, 255)' size='1rem' style={{ width: 'inherit', height: 'inherit' }} />
          </div>
        </Root>
      );
    }

    // 브라우저 가로폭이 1024보다 작거나 viewrowstate가 false일때
    if (!(size.innerSize.W > 1024 || viewRowState)) {
      return null;
    }

    // viewrowstate가 false이고 왼쪽 데이터가 있을때
    if (!viewRowState) {
      return (
        <Root loading={false}>
          <div className='emptyData'>
            <img src={illustrator} alt='noData' />
            <span>{t('왼쪽 목록에서 센서를 선택해주세요')}</span>
          </div>
        </Root>
      );
    }

    // 브라우저 가로폭이 1024보다 크거나 viewrowstate가 true일때
    return (
      <Root loading={isLoadingSubSensor || isFetchingSubSensor} className='headerLeft'>
        <SearchOptions align='left'>
          <div className='inputsWrapperGroup'>
            {size.innerSize.W <= 1024 && <BackButton func={() => setViewRowState(false)} />}
            <div className='inputsWrapper sensor-results'>
              <div className='sensor-result-header'>
                <span>{t('측정일')}</span>
                <div>{rowState.checkTime}</div>
              </div>
              <div className='sensor-result-data'>
                <span>{t(mGasInfo.name)}</span>
                <div>
                  <span>{t('평균')}</span>
                  {getRowStateInfo('avg')}
                </div>
                <div>
                  <span>{t('최소')}</span>
                  {getRowStateInfo('min')}
                </div>
                <div>
                  <span>{t('최대')}</span>
                  {getRowStateInfo('max')}
                </div>
              </div>
            </div>
          </div>
          <div className='inputsWrapper sensorOverflow'>
            <div className='secondSearchOption mgas-responsive'>
              <div className='flex-basic shrink-0'>
                <SelectBox options={SENSOR_TIME} defaultOption={sensorTime.cdName} state={sensorTime} setState={setSensorTime} stateKey='time' />
              </div>
              {sensorTime.time !== '1' && (
                <div>
                  {sensorRange?.srCd === 'O2' ? (
                    <div className='flex-basic flex-col range-wrapper'>
                      <div className='range-top'>
                        <div className='range-status'>{t('좋음')}</div>
                        <div className='range-status'>{t('주의')}</div>
                        <div className='range-status'>{t('경고')}</div>
                        <div className='range-status'>{t('위험')}</div>
                      </div>
                      <div className='range-bottom'>
                        <div className='range-left flex-col'>
                          <span>{t('상한가')}</span>
                          <span>{t('하한가')}</span>
                        </div>
                        <div className='range-center'>
                          {O2_RANGE.map((item: { level: number; lower: number[]; upper: number[] }) => (
                            <div className='range-guage-item' key={item.level}>
                              <div className='guage-item-top'>
                                <span>{item.level !== 3 ? (item.upper[0] !== -1 ? item.upper[0] : '~') : `${item.upper[0]} ${t('초과')}`}</span>
                                {item.level !== 0 && item.level !== 3 && <span>~</span>}
                                <span>{item.level !== 0 ? (item.upper[1] !== -1 ? item.upper[1] : '~') : `${item.upper[1]} ${t('이하')}`}</span>
                              </div>
                              <hr />
                              <div className='guage-item-bottom'>
                                <span>{item.level !== 3 ? (item.lower[0] !== -1 ? item.lower[0] : '~') : `${item.lower[0]} ${t('미만')}`}</span>
                                {item.level !== 0 && item.level !== 3 && <span>~</span>}
                                <span>{item.level !== 0 ? (item.lower[1] !== -1 ? item.lower[1] : '~') : `${item.lower[1]} ${t('이상')}`}</span>
                              </div>
                            </div>
                          ))}
                        </div>
                        <div className='range-right'>
                          <span>(% vol)</span>
                        </div>
                      </div>
                    </div>
                  ) : (
                    /** 센서범위 지정값이 있는경우만 표시 */
                    sensorRange && (
                      <div className='guide levels'>
                        <div className='value level0 flex-basic'>
                          <div className='flex-col'>
                            <span className='value-title'>{t('좋음')}</span>
                            <span>
                              {sensorRange.level1} {t('미만')}
                            </span>
                          </div>
                        </div>
                        <div className='value level1 flex-basic'>
                          <div className='flex-col'>
                            <span className='value-title'>{t('주의')}</span>
                            <span>
                              {sensorRange.level1} {t('이상')}
                            </span>
                          </div>
                        </div>
                        <div className='value level2 flex-basic'>
                          <div className='flex-col'>
                            <span className='value-title'>{t('경고')}</span>
                            <span>
                              {sensorRange.level2} {t('이상')}
                            </span>
                          </div>
                        </div>
                        <div className='value level3 flex-basic'>
                          <div className='flex-col'>
                            <span className='value-title'>{t('위험')}</span>
                            <span>
                              {sensorRange.level3} {t('이상')}
                            </span>
                          </div>
                        </div>
                      </div>
                    )
                  )}
                </div>
              )}
              <div className='flex-basic iconBtnGroup'>
                <BtnGhost onClick={() => onClickChart({ tableData: subSensor, type: 'lineChart2' })}>
                  <span className='material-symbols-rounded'>monitoring</span>
                  {t('차트')}
                </BtnGhost>
                {renderExcelBtn(onClickExcelSave2)}
                {/* {renderPrintBtn(onClickPrint2)} */}
              </div>
            </div>
          </div>
        </SearchOptions>
        {isLoadingSubSensor || isFetchingSubSensor ? (
          <LoadingModalBackground>
            <PulseLoader className='flex-center' color='rgb(0, 122, 255)' size='1rem' style={{ width: 'inherit', height: 'inherit' }} />
          </LoadingModalBackground>
        ) : subSensor?.length > 0 ? (
          <TuiGridTable tableState={subSensor} columns={subColumns} listCount={Number(listCount[LIST_COUNT])} pageNum={1} gridId='grid1' />
        ) : null}
        <div className='hide'>
          <TuiGridTable tableState={exportTableState} columns={exportColumns} listCount={Number(listCount[LIST_COUNT])} setExcelBtn={setExcelBtn2} excelBtn={excelBtn2} fileName={getFileName('sub')} />
        </div>
      </Root>
    );
  };

  const loadingMain = isLoadingMainSensor || isFetchingMainSensor;

  if (isErrorMainSensor) return <IssueGuide />;

  return (
    <div className='contents'>
      <Tab tabList={newTabList || tabInfo.tabList} currentTabMrCd={currentTabMrCd} setCurrentTabMrCd={setCurrentTabMrCd} onClickTab={onClickTab} size={size} />
      <div className={`content-container ${size.innerSize.W > 1024 ? 'twoColumn column-55 mediaSet768' : 'oneColumn'}`}>
        <Root loading={loadingMain} className={size.innerSize.W > 1024 || !viewRowState ? 'showRoot' : 'hideRoot'}>
          <SearchOptions>
            {size.innerSize.W > 1024 ? (
              <>
                <div className='inputsWrapper'>
                  <div className='inputForm-col withLabelComCf'>
                    <label htmlFor='fName'>{t('장비')}</label>
                    <SelectBox
                      options={fNameList}
                      defaultOption={t('전체')}
                      state={fName}
                      setState={setFName}
                      stateKey='fName'
                      filterbar='filter-1-left'
                      initiateKey={fName.fName}
                      optionHeight='height-md'
                    />
                  </div>
                  <div className='inputForm-row-fit'>
                    <span className='calendarLabel'>{t('조회일자')}</span>
                    <div className='inputForm-col'>
                      <div className='flex-basic'>
                        <input id='startInput' type='text' value={searchOption.start} onChange={(e) => onChangeRangeInput(e, 'start', setRangeState, setSearchOption)} />
                        <span className='inputDash'> ~ </span>
                        <input id='endInput' type='text' value={searchOption.end} onChange={(e) => onChangeRangeInput(e, 'end', setRangeState, setSearchOption)} />
                      </div>
                      {visibleRangePicker && (
                        <div className='rangePickerWrapper'>
                          <RangePicker state={rangeState} setState={setRangeState} setVisible={setVisibleRangePicker} />
                        </div>
                      )}
                    </div>
                    <button type='button' className='date-picker-btn' onClick={onClickRangeInput}>
                      <span className='material-symbols-rounded'>calendar_month</span>
                    </button>
                  </div>
                </div>
                <div className='inputsWrapper'>
                  <div className='secondSearchOption'>
                    <div className='flex-basic textBtnGroup'>
                      <ShortcutButton icon='search' buttonText='검색' shortcut='F9' onClick={onClickSearch} />
                      <BtnGhost onClick={onClickInitiateSearchOption}>{t('초기화')}</BtnGhost>
                      <div className='searchResult'>
                        {t('총')}
                        <span>{mainSensor?.length || 0}</span>
                        {t('개')}
                      </div>
                      <div className='inputForm-row'>
                        <div className='inputForm-col withLabelComCf'>
                          <label htmlFor='useYn'>{t('보기 설정')}</label>
                          <SelectBox
                            options={listCountComCdList}
                            defaultOption={listCount.cdName}
                            state={listCount}
                            setState={setListCount}
                            stateKey={LIST_COUNT}
                            initiateKey={listCount[LIST_COUNT]}
                            setTableLinesAPI={patchUserMenuAPI}
                            optionHeight='height-sm'
                            rsearch
                          />
                        </div>
                      </div>
                    </div>

                    <div className='flex-basic iconBtnGroup'>
                      <BtnGhost onClick={() => onClickChart({ tableData: mainSensor, type: 'lineChart' })}>
                        <span className='material-symbols-rounded'>monitoring</span>
                        {t('차트')}
                      </BtnGhost>
                      {renderExcelBtn(onClickExcelSave)}
                      {/* {renderPrintBtn(onClickPrint)} */}
                    </div>
                  </div>
                </div>
              </>
            ) : (
              <div className='filter-wrapper'>
                <div className='inputsWrapper'>
                  <div className='flex-basic iconBtnGroup'>
                    <BtnGhost onClick={onClickFilter} className='filter'>
                      <span className='material-symbols-rounded'>filter_alt</span>
                      {t('검색 필터')}
                    </BtnGhost>
                    <BtnGhost onClick={() => onClickChart({ tableData: mainSensor, type: 'lineChart' })}>
                      <span className='material-symbols-rounded'>monitoring</span>
                      {t('차트')}
                    </BtnGhost>
                    {renderExcelBtn(onClickExcelSave)}
                  </div>
                </div>
              </div>
            )}
          </SearchOptions>
          {loadingMain ? (
            <LoadingModalBackground>
              <PulseLoader className='flex-center' color='rgb(0, 122, 255)' size='1rem' style={{ width: 'inherit', height: 'inherit' }} />
            </LoadingModalBackground>
          ) : mainSensor?.length > 0 ? (
            <TuiGridTable
              tableState={mainSensor}
              columns={mainColumns}
              listCount={Number(listCount[LIST_COUNT])}
              setExcelBtn={setExcelBtn}
              excelBtn={excelBtn}
              onClickRow={onClickRow}
              fileName={getFileName('main')}
              pageNum={1}
              gridId='grid2'
            />
          ) : (
            <div className='emptyData'>
              <img src={nodataIllustrator} alt='noData' />
              <span>{t('해당 일자의 데이터가 존재하지 않습니다')}</span>
            </div>
          )}
        </Root>
        {renderSubTable()}
      </div>
      {/* <Portal openModal={loading}>
        <LoadingModalBackground>
          <PulseLoader className='flex-center' color='rgb(0, 122, 255)' size='1rem' style={{ width: 'inherit', height: 'inherit' }} />
        </LoadingModalBackground>
      </Portal> */}
      <Portal openModal={openModal?.status}>
        {(openModal?.type === 'lineChart' || openModal?.type === 'lineChart2') && (
          <LineChartModal
            setOpenModal={setOpenModal}
            chartData={openModal.type === 'lineChart' ? mainChartDataReverse : subChartDataReverse}
            datasets={openModal.type === 'lineChart' ? mainDataSet : subDataSet}
            title={t(`${mGasInfo.name} 측정기`)}
            yAxisUnit={mGasInfo.tableKey.value === 'CO2' ? getCO2ValueType(sensorRange?.unit) : mGasInfo.tableKey.valueType}
          />
        )}
      </Portal>
      {size.innerSize.W <= 1024 && (
        <Portal openModal>
          <SearchFilterModal openModal={openModal} setOpenModal={setOpenModal} onClickSearch={onClickSearch} onClickReset={onClickInitiateSearchOption}>
            <div className='filter-option-wrapper'>
              <div className='filter-option-col filter-underline filter-option-date'>
                <span className='calendarLabel'>{t('조회일자')}</span>
                <div className='inputForm-col'>
                  <div className='flex-basic' role='presentation' onClick={onClickRangeInput}>
                    <input id='startInput' type='text' readOnly value={searchOption.start} onChange={(e) => onChangeRangeInput(e, 'start', setRangeState, setSearchOption)} />
                    <span className='inputDash'> ~ </span>
                    <input id='endInput' type='text' readOnly value={searchOption.end} onChange={(e) => onChangeRangeInput(e, 'end', setRangeState, setSearchOption)} />
                  </div>
                  {visibleRangePicker && (
                    <div className='rangePickerWrapper'>
                      <RangePicker state={rangeState} setState={setRangeState} setVisible={setVisibleRangePicker} />
                    </div>
                  )}
                </div>
              </div>
              <div className='filter-option-cont'>
                <div className='filter-option-row'>
                  <label htmlFor='useYn'>{t('보기 설정')}</label>
                  <SelectBox
                    options={listCountComCdList}
                    defaultOption={listCount.cdName}
                    state={listCount}
                    setState={setListCount}
                    stateKey={LIST_COUNT}
                    initiateKey={listCount[LIST_COUNT]}
                    setTableLinesAPI={patchUserMenuAPI}
                    optionHeight='height-sm'
                    rsearch
                  />
                </div>
                <div className='filter-option-row'>
                  <label htmlFor='fName'>{t('장비')}</label>
                  <SelectBox
                    options={fNameList}
                    defaultOption={t('전체')}
                    state={fName}
                    setState={setFName}
                    stateKey='fName'
                    filterbar='filter-1-left'
                    initiateKey={fName.fName}
                    optionHeight='height-md'
                  />
                </div>
              </div>
            </div>
          </SearchFilterModal>
        </Portal>
      )}
    </div>
  );
};

export default Ssensor7;
